import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, BookOpen, Cpu, Image as ImageIcon, KeyRound, Loader2, MessageSquare, Moon, ShieldCheck, Sparkles, Sun, Wrench, Zap } from "lucide-react";
import { ICON_CONFIG } from "../config";

const fieldAnim = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -6 },
  transition: { duration: 0.24, ease: "easeOut" },
};

const pageAnim = {
  initial: { opacity: 0, y: 24, scale: 0.986 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -14, scale: 0.986 },
  transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] },
};

const landingCopyAnim = {
  initial: {},
  animate: { transition: { staggerChildren: 0.17, delayChildren: 0.14 } },
};

const landingItemAnim = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.62, ease: [0.22, 1, 0.36, 1] } },
};

export default function AuthView({
  view,
  isDark,
  onThemeToggle,
  loading,
  authForm,
  setAuthForm,
  handleAuth,
  handleSendPhoneCode,
  handleSendPasswordResetCode,
  handleCompleteRegister,
  handlePhoneCodeLogin,
  handlePasswordReset,
  phoneCodeStatus,
  phoneCodeCooldown = 0,
  setView,
}) {
  const isLanding = view === "landing";
  const isRegister = view === "register";
  const isBindPhone = view === "bind-phone";
  const isPhoneLogin = view === "phone-login";
  const isResetPassword = view === "reset-password";
  const codeButtonText = phoneCodeCooldown > 0 ? `${phoneCodeCooldown}s 后重发` : "获取验证码";
  const codeButtonDisabled = loading || phoneCodeCooldown > 0;
  const handleLandingPointerMove = (event) => {
    const target = event.currentTarget;
    const rect = target.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    target.style.setProperty("--pointer-x", `${x}%`);
    target.style.setProperty("--pointer-y", `${y}%`);
    target.style.setProperty("--tilt-x", `${(50 - y) / 18}deg`);
    target.style.setProperty("--tilt-y", `${(x - 50) / 20}deg`);
  };

  const authTitle = isRegister
    ? "创建账号"
    : isBindPhone
      ? "绑定手机号"
      : isPhoneLogin
        ? "验证码登录"
        : isResetPassword
          ? "找回密码"
        : "欢迎回来";

  return (
    <main className={`auth-screen ${isDark ? "theme-dark" : "theme-light"} ${isLanding ? "auth-landing-active" : ""}`}>
      <button type="button" className="auth-theme-toggle" onClick={onThemeToggle} aria-label="切换主题">
        {isDark ? <Sun size={18} /> : <Moon size={18} />}
      </button>

      <AnimatePresence mode="popLayout">
      {isLanding ? (
        <motion.section key="landing" className="landing-shell" onPointerMove={handleLandingPointerMove} {...pageAnim}>
          <div className="landing-ambient" aria-hidden="true">
            <span />
            <span />
            <span />
          </div>
          <motion.div className="landing-copy" variants={landingCopyAnim} initial="initial" animate="animate">
            <motion.div className="landing-brand" variants={landingItemAnim}>
              <div className="brand-mark">
                {ICON_CONFIG.logo ? <img src={ICON_CONFIG.logo} alt="LumaNova" /> : <Zap size={22} />}
              </div>
              <span>LumaNova AI</span>
            </motion.div>
            <motion.p className="landing-eyebrow" variants={landingItemAnim}>Where Ideas Take Shape</motion.p>
            <motion.div className="landing-title-stack" aria-label="LumaNova AI 工作台" variants={landingItemAnim}>
              <strong>LumaNova</strong>
              <span>AI Agent Workspace</span>
              <small>把灵感、资料和执行过程放在同一个安静的空间里，让 AI 更自然地帮你完成事情。</small>
            </motion.div>
            <motion.p className="landing-subtitle" variants={landingItemAnim}>
              从日常问答到知识库检索、图片处理和接口调用，都可以在这里顺着对话继续推进。
            </motion.p>
            <motion.div className="landing-actions" variants={landingItemAnim}>
              <button type="button" className="landing-primary" onClick={() => setView("login")}>
                进入登录
                <ArrowRight size={18} />
              </button>
              <button type="button" className="landing-secondary" onClick={() => setView("phone-login")}>
                手机号登录
              </button>
            </motion.div>
            <motion.div className="landing-stats" aria-label="平台能力" variants={landingItemAnim}>
              <span>Agent Runtime</span>
              <span>Vision</span>
              <span>RAG Memory</span>
              <span>OpenAI SDK</span>
            </motion.div>
          </motion.div>

          <motion.div
            className="landing-product"
            initial={{ opacity: 0, x: 34, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, x: 0, y: 0, scale: 1 }}
            exit={{ opacity: 0, x: 18 }}
            transition={{ duration: 0.82, ease: [0.22, 1, 0.36, 1], delay: 0.42 }}
          >
            <div className="product-halo" aria-hidden="true" />
            <div className="product-window">
              <div className="product-topbar">
                <div className="product-dots"><span /><span /><span /></div>
                <span>LumaNova Command</span>
              </div>
              <div className="product-body">
                <div className="product-sidebar">
                  <span className="active"><Sparkles size={13} /> Agent</span>
                  <span><BookOpen size={13} /> RAG</span>
                  <span><ImageIcon size={13} /> Image</span>
                </div>
                <div className="product-chat">
                  <div className="cinema-strip" aria-hidden="true"><span /><span /><span /><span /></div>
                  <div className="chat-line user">把这段规则整理成客服可执行话术</div>
                  <div className="chat-line tool"><BookOpen size={14} /> 正在检索知识库</div>
                  <div className="chat-line tool second"><Cpu size={14} /> 正在调度工具</div>
                  <div className="chat-line bot">已压缩成可直接使用的回复，并保留关键边界。</div>
                </div>
              </div>
            </div>
            <div className="capability-strip">
              <span><Wrench size={14} /> Tool trace</span>
              <span><ImageIcon size={14} /> Vision</span>
              <span><KeyRound size={14} /> API keys</span>
              <span><ShieldCheck size={14} /> Quota</span>
            </div>
          </motion.div>
        </motion.section>
      ) : (
      <motion.section key={view} className="auth-panel" {...pageAnim}>
        <button type="button" className="auth-back" onClick={() => setView("landing")}>
          返回首页
        </button>
        <div className="brand-mark brand-mark-large">
          {ICON_CONFIG.logo ? <img src={ICON_CONFIG.logo} alt="LumaNova" /> : <Zap size={32} />}
        </div>
        <p className="auth-kicker">LumaNova AI</p>
        <h1>{authTitle}</h1>

        <AnimatePresence mode="sync">
          {view === "login" && (
            <motion.div key="login" className="auth-fields" {...fieldAnim}>
              <input
                value={authForm.username}
                placeholder="用户名或手机号"
                onChange={(e) => setAuthForm({ ...authForm, username: e.target.value })}
              />
              <input
                type="password"
                value={authForm.password}
                placeholder="密码"
                onChange={(e) => setAuthForm({ ...authForm, password: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && handleAuth("login")}
              />
              {phoneCodeStatus?.msg && (
                <p className={`auth-message ${phoneCodeStatus.success ? "success" : "error"}`}>{phoneCodeStatus.msg}</p>
              )}
              <button type="button" onClick={() => handleAuth("login")} disabled={loading}>
                {loading ? <Loader2 className="spin" /> : "登录"}
              </button>
              <button type="button" className="link-button" onClick={() => setView("phone-login")}>
                手机号验证码登录
              </button>
              <button type="button" className="link-button" onClick={() => setView("reset-password")}>
                忘记密码
              </button>
              <button type="button" className="link-button" onClick={() => setView("register")}>
                注册新账号
              </button>
            </motion.div>
          )}

          {isRegister && (
            <motion.div key="register" className="auth-fields" {...fieldAnim}>
              <input
                value={authForm.username}
                placeholder="用户名"
                onChange={(e) => setAuthForm({ ...authForm, username: e.target.value })}
              />
              <input
                type="password"
                value={authForm.password}
                placeholder="密码"
                onChange={(e) => setAuthForm({ ...authForm, password: e.target.value })}
              />
              <input
                type="password"
                value={authForm.confirmPassword}
                placeholder="确认密码"
                onChange={(e) => setAuthForm({ ...authForm, confirmPassword: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && handleAuth("register")}
              />
              {phoneCodeStatus?.msg && (
                <p className={`auth-message ${phoneCodeStatus.success ? "success" : "error"}`}>{phoneCodeStatus.msg}</p>
              )}
              <button type="button" onClick={() => handleAuth("register")} disabled={loading}>
                {loading ? <Loader2 className="spin" /> : "继续绑定手机号"}
              </button>
              <button type="button" className="link-button" onClick={() => setView("login")}>
                返回登录
              </button>
            </motion.div>
          )}

          {isBindPhone && (
            <motion.div key="bind-phone" className="auth-fields" {...fieldAnim}>
              <p className="auth-step-copy">为了保障账号安全，请绑定手机号完成注册。</p>
              <input
                value={authForm.phone}
                placeholder="手机号"
                inputMode="tel"
                maxLength={11}
                onChange={(e) => setAuthForm({ ...authForm, phone: e.target.value.replace(/\D/g, "").slice(0, 11) })}
              />
              <div className="code-row">
                <input
                  value={authForm.code}
                  placeholder="验证码"
                  inputMode="numeric"
                  onChange={(e) => setAuthForm({ ...authForm, code: e.target.value })}
                  onKeyDown={(e) => e.key === "Enter" && handleCompleteRegister()}
                />
                <button type="button" className="secondary-button" onClick={handleSendPhoneCode} disabled={codeButtonDisabled}>
                  <MessageSquare size={17} />
                  {codeButtonText}
                </button>
              </div>
              {phoneCodeStatus?.msg && (
                <p className={`auth-message ${phoneCodeStatus.success ? "success" : "error"}`}>
                  {phoneCodeStatus.msg}
                  {phoneCodeStatus.debug_code ? `：${phoneCodeStatus.debug_code}` : ""}
                </p>
              )}
              <button type="button" onClick={handleCompleteRegister} disabled={loading}>
                {loading ? <Loader2 className="spin" /> : "完成注册"}
              </button>
              <button type="button" className="link-button" onClick={() => setView("register")}>
                返回上一步
              </button>
            </motion.div>
          )}

          {isPhoneLogin && (
            <motion.div key="phone-login" className="auth-fields" {...fieldAnim}>
              <input
                value={authForm.phone}
                placeholder="手机号"
                inputMode="tel"
                maxLength={11}
                onChange={(e) => setAuthForm({ ...authForm, phone: e.target.value.replace(/\D/g, "").slice(0, 11) })}
              />
              <div className="code-row">
                <input
                  value={authForm.code}
                  placeholder="验证码"
                  inputMode="numeric"
                  onChange={(e) => setAuthForm({ ...authForm, code: e.target.value })}
                  onKeyDown={(e) => e.key === "Enter" && handlePhoneCodeLogin()}
                />
                <button type="button" className="secondary-button" onClick={handleSendPhoneCode} disabled={codeButtonDisabled}>
                  <MessageSquare size={17} />
                  {codeButtonText}
                </button>
              </div>
              {phoneCodeStatus?.msg && (
                <p className={`auth-message ${phoneCodeStatus.success ? "success" : "error"}`}>
                  {phoneCodeStatus.msg}
                  {phoneCodeStatus.debug_code ? `：${phoneCodeStatus.debug_code}` : ""}
                </p>
              )}
              <button type="button" onClick={handlePhoneCodeLogin} disabled={loading}>
                {loading ? <Loader2 className="spin" /> : "验证码登录"}
              </button>
              <button type="button" className="link-button" onClick={() => setView("login")}>
                返回账号登录
              </button>
            </motion.div>
          )}

          {isResetPassword && (
            <motion.div key="reset-password" className="auth-fields" {...fieldAnim}>
              <p className="auth-step-copy">使用账号绑定的手机号验证身份并设置新密码。</p>
              <input
                value={authForm.phone}
                placeholder="绑定手机号"
                inputMode="tel"
                maxLength={11}
                onChange={(e) => setAuthForm({ ...authForm, phone: e.target.value.replace(/\D/g, "").slice(0, 11) })}
              />
              <div className="code-row">
                <input
                  value={authForm.code}
                  placeholder="验证码"
                  inputMode="numeric"
                  onChange={(e) => setAuthForm({ ...authForm, code: e.target.value })}
                />
                <button type="button" className="secondary-button" onClick={handleSendPasswordResetCode} disabled={codeButtonDisabled}>
                  <MessageSquare size={17} />
                  {codeButtonText}
                </button>
              </div>
              <input
                type="password"
                value={authForm.password}
                placeholder="新密码"
                onChange={(e) => setAuthForm({ ...authForm, password: e.target.value })}
              />
              <input
                type="password"
                value={authForm.confirmPassword}
                placeholder="确认新密码"
                onChange={(e) => setAuthForm({ ...authForm, confirmPassword: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && handlePasswordReset()}
              />
              {phoneCodeStatus?.msg && (
                <p className={`auth-message ${phoneCodeStatus.success ? "success" : "error"}`}>
                  {phoneCodeStatus.msg}
                  {phoneCodeStatus.debug_code ? `：${phoneCodeStatus.debug_code}` : ""}
                </p>
              )}
              <button type="button" onClick={handlePasswordReset} disabled={loading}>
                {loading ? <Loader2 className="spin" /> : "重置密码"}
              </button>
              <button type="button" className="link-button" onClick={() => setView("login")}>
                返回账号登录
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.section>
      )}
      </AnimatePresence>
    </main>
  );
}
